import { css } from '@emotion/css';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../features';
import { actions } from '../../features/bestFriends';
import { sendEvent } from '../../lib/google-analytics';
import { ReportCategory } from '../../models/ReportCategory';
import { BackButton } from '../shared/buttons/BackButton';
import { ReportForm } from '../shared/report/ReportForm';
import { DialogRoute } from '../shared/routes/DialogRoute';
import { ReportFailedDialog } from './ReportFailedDialog';

const reportFormStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  background: #f5f1e3;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;

  * {
    font-family: system-ui, -apple-system, sans-serif;
    font-weight: bold;
  }
  header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 52px;
    padding: 0 4px;

    .space {
      width: 48px;
      height: 100%;
    }
  }
  h1 {
    font-size: 16px;
    color: #4d3906;
  }
  .body {
    flex: 1 1 auto;
    width: 100%;
    overflow-y: scroll;
  }
`;

export const BestFriendReportForm: React.FC = () => {
  const { t, i18n } = useTranslation('best_friends');
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { reportStatus, reportErrorType, profiles } = useSelector(
    (state: RootState) => state.bestFriends
  );
  const dispatch = useDispatch();

  const profile = profiles.find(p => p.userId === id);
  const language = i18n.language;

  useEffect(() => {
    if (reportStatus === 'succeeded') {
      history.replace(`/players/best-friends/report-completed/${id}`);
    }
  }, [id, reportStatus, history, dispatch]);

  useEffect(() => {
    if (reportStatus === 'failed') {
      history.push(`/players/best-friends/report/${id}/failed?type=${reportErrorType}`);
    }
  }, [history, id, reportErrorType, reportStatus]);

  const handleSubmit = (category: ReportCategory, reason: string) => {
    if (profile) {
      dispatch(
        actions.reportFriend({ type: 'user_profile', category, language, reason, ugc: profile })
      );
    }
    sendEvent('best_friends_report', 'click');
  };

  return (
    <>
      <div className={reportFormStyles}>
        <header>
          <BackButton ariaLabel={t('aria_button_back')} />
          <h1>{t('report_spam')}</h1>
          <div className="space" />
        </header>
        <div className="body">
          <ReportForm onSubmit={handleSubmit} isLoading={reportStatus === 'loading'} />
        </div>
      </div>

      <DialogRoute path="/players/best-friends/report/:id/failed">
        <ReportFailedDialog />
      </DialogRoute>
    </>
  );
};
